import { create } from 'zustand';
import type { ArmSlot, BanquettePolyline, PanelRole } from '@estimator/shared';

const SLOT_COLORS = [
  '#e74c3c', '#3498db', '#2ecc71', '#9b59b6', '#f39c12',
  '#1abc9c', '#e67e22', '#e91e63', '#00bcd4', '#8bc34a',
];

let slotCounter = 0;

/** Arm-specific labels for the shared front/side/back panel roles. */
const ROLE_LABELS: Record<PanelRole, string> = {
  front: 'Main Wrap',
  side: 'End Caps',
  back: 'Back',
};

interface ArmSlotStore {
  slots: ArmSlot[];
  activeSlotId: string | null;
  setSlots: (slots: ArmSlot[]) => void;
  setActiveSlot: (id: string | null) => void;
  addSlot: (role: PanelRole, options?: { autoPairSide?: boolean }) => void;
  updateSlot: (id: string, updates: Partial<ArmSlot>) => void;
  removeSlot: (id: string) => void;
  addPolylineToSlot: (slotId: string, polyline: BanquettePolyline) => void;
  removePolyline: (slotId: string, index: number) => void;
  clearAll: () => void;
}

export const useArmSlotStore = create<ArmSlotStore>((set) => ({
  slots: [],
  activeSlotId: null,

  setSlots: (slots) => set({ slots }),
  setActiveSlot: (id) => set({ activeSlotId: id }),

  addSlot: (role, options) =>
    set((state) => {
      const baseColor = SLOT_COLORS[state.slots.length % SLOT_COLORS.length];

      const makeSlot = (r: PanelRole, name: string, color: string): ArmSlot => {
        slotCounter++;
        return {
          id: `arm_slot_${slotCounter}_${Date.now()}`,
          name,
          material_id: null,
          color,
          calc_mode: 'linear',
          panel_role: r,
          polylines: [],
          fabric_wrap_value: null,
          fabric_staple_allowance: 1,
        };
      };

      const roleCount = state.slots.filter((s) => s.panel_role === role).length;
      const baseName = `${ROLE_LABELS[role]} ${roleCount + 1}`;

      const primary = makeSlot(role, baseName, baseColor);
      const newSlots: ArmSlot[] = [primary];

      if (role === 'front' && options?.autoPairSide) {
        const sidePaired = makeSlot('side', `${baseName} — end caps`, baseColor);
        newSlots.push(sidePaired);
      }

      return {
        slots: [...state.slots, ...newSlots],
        activeSlotId: primary.id,
      };
    }),

  updateSlot: (id, updates) =>
    set((state) => ({
      slots: state.slots.map((s) => (s.id === id ? { ...s, ...updates } : s)),
    })),

  removeSlot: (id) =>
    set((state) => ({
      slots: state.slots.filter((s) => s.id !== id),
      activeSlotId: state.activeSlotId === id ? null : state.activeSlotId,
    })),

  addPolylineToSlot: (slotId, polyline) =>
    set((state) => ({
      slots: state.slots.map((s) =>
        s.id === slotId ? { ...s, polylines: [...s.polylines, polyline] } : s,
      ),
    })),

  removePolyline: (slotId, index) =>
    set((state) => ({
      slots: state.slots.map((s) =>
        s.id === slotId
          ? { ...s, polylines: s.polylines.filter((_, i) => i !== index) }
          : s,
      ),
    })),

  clearAll: () => set({ slots: [], activeSlotId: null }),
}));

export { ROLE_LABELS as ARM_ROLE_LABELS };
